import type { SlideshowInterface } from '~/types/AppData/PageContent/SlideshowInterface';
import type { ArticleInterface } from '~/types/AppData/PageContent/ArticleInterface';
import type { BlockType } from '~/types/AppData/Blocks/BlockType';
import { getHasSlideshow } from '~/utilities/getHasSlideshow';

/**
 * Filters the blocks of an article based on the slide number.
 * If the given article does not include a slideshow, all body blocks
 * of the article will be returned. Otherwise, only the blocks
 * corresponding to the specified `currentSlide` will be included.
 *
 * @param {ArticleInterface} article - The article object which contains body blocks.
 * @param {SlideshowInterface} [slideshow] - The slideshow object (optional).
 * @param {number} [currentSlide] - The number of the current slide to filter by (optional).
 * @returns {BlockType[]} The array of blocks corresponding to the current slide or
 *                         all blocks if no slideshow is present.
 */
export const maybeGetSlide = (
  article?: ArticleInterface,
  slideshow?: SlideshowInterface,
  currentSlide?: number
): BlockType[] | [] => {
  if (!article) {
    return [];
  }

  if (!getHasSlideshow(article, slideshow)) {
    return article.body;
  }

  let currentCount: number = 1;

  return article.body.filter((block) => {
    if (block.type === 'page-break') {
      currentCount++;
      return false;
    }

    return currentCount === currentSlide;
  });
};
